import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  openLoginModal?: boolean;
}

/**
 * ProtectedRoute - SIMPLIFICADO
 * 
 * Responsabilidad ÚNICA: Verificar que el usuario está autenticado
 * 
 * - Si NO hay usuario → redirige a redirectTo (por defecto "/") y opcionalmente abre modal de login
 * - Si SÍ hay usuario → renderiza contenido
 * 
 * La redirección por rol se maneja en App.tsx (post-login)
 */
const ProtectedRoute = ({
  children,
  redirectTo = "/",
  openLoginModal = false,
}: ProtectedRouteProps) => {
  const { user, isLoading, openAuthModal } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Si aún está cargando, no hacer nada
    if (isLoading) return;

    // Si no hay usuario, redirigir y opcionalmente abrir el modal de login
    if (!user) {
      navigate(redirectTo, { replace: true });
      if (openLoginModal) {
        openAuthModal("login");
      }
    }
  }, [user, isLoading, navigate, redirectTo, openLoginModal, openAuthModal]);

  // Mostrar loader mientras carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Si no hay usuario, no renderizar nada (se redirigirá a home)
  if (!user) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
